import type { TOC } from '@ember/component/template-only';

import AppLoadedEventsComponent from 'webapp/components/app-loaded-events';
import AppNotificationsComponent from 'webapp/components/app-notifications';
import CommonRotateDeviceComponent from 'webapp/components/common/rotate-device';
import DataManagerConnectedManagerComponent from 'webapp/components/data-manager/connected/manager';
import GenieLauncherComponent from 'webapp/components/genie/launcher/index';
import GeniePanelComponent from 'webapp/components/genie/panel/index';
import GenieSettingsModalComponent from 'webapp/components/genie/settings-modal';
import InfolayersComponent from 'webapp/components/infolayers';
import LineageExplorerHostComponent from 'webapp/components/lineage-explorer-host';
import MetricInfoManagerComponent from 'webapp/components/metric-info/manager';
import MetricMoverModalComponent from 'webapp/components/metric-mover/modal';
import NavigationMainComponent from 'webapp/components/navigation/main';
import NavigationSectionComponent from 'webapp/components/navigation/section';
import NpsFormComponent from 'webapp/components/nps/form';
import OnboardingGuidedTourBannerComponent from 'webapp/components/onboarding/guided-tour/banner';
import SourceConnectContainerComponent from 'webapp/components/source/connect/container';
import UserManagementManagerComponent from 'webapp/components/user-management/manager';
import type ApplicationController from 'webapp/controllers/application';

interface Args {
	controller: ApplicationController;
}

const ApplicationRouteTemplate: TOC<Args> = <template>
	<div id="wrapper" class={{@controller.wrapperClass}}>

		{{#if @controller.hasNavigationMain}}

			<NavigationMainComponent @onIsExpandedChange={{@controller.onIsExpandedChange}} />

			{{#unless @controller.skipNavigationSection}}
				<NavigationSectionComponent />
			{{/unless}}

		{{/if}}

		<div id="content-wrapper" class="smart-center-pos">

			<div class="container">
				{{#if @controller.showOutlet}}
					{{outlet}}
				{{/if}}

				{{#if @controller.isNpsFeedbackFormShown}}
					<NpsFormComponent />
				{{/if}}
			</div>

		</div>

	</div>

	{{#if @controller.showAppNotifications}}
		<AppNotificationsComponent />
	{{/if}}

	<AppLoadedEventsComponent />

	{{#each @controller.popupService.popupStack as |popup|}}
		{{#let (component popup.wrapperComponent) as |WrapperComponentName|}}
			<WrapperComponentName @model={{popup}}>
				{{#let (component popup.innerComponent) as |TemplateComponentName|}}
					<TemplateComponentName @model={{popup}} />
				{{/let}}
			</WrapperComponentName>
		{{/let}}
	{{/each}}

	{{#if @controller.shouldLoadSourceConnect}}
		<MetricMoverModalComponent />
		<SourceConnectContainerComponent />
		<DataManagerConnectedManagerComponent />
		<UserManagementManagerComponent />
		<MetricInfoManagerComponent />
	{{/if}}

	{{#if @controller.flowTourOnboardingService.isBannerShown}}
		<OnboardingGuidedTourBannerComponent />
	{{/if}}

	{{#if @controller.shouldShowGeniePanel}}
		<GeniePanelComponent @hasLauncher={{@controller.shouldShowGenieLauncher}} />
	{{/if}}

	{{#if @controller.shouldShowGenieLauncher}}
		<GenieLauncherComponent />
	{{/if}}

	{{! Genie's chart card opens the lineage explorer through ai-chat, on any page Genie can be used from. }}
	{{#if @controller.shouldMountGenieLineageExplorer}}
		<LineageExplorerHostComponent
			@onReady={{@controller.aiChatService.registerLineageOpener}}
			@onDestroy={{@controller.aiChatService.unregisterLineageOpener}}
		/>
	{{/if}}

	{{#if @controller.genieSettingsTab}}
		<GenieSettingsModalComponent
			@activeTab={{@controller.genieSettingsTab}}
			@onTabChange={{@controller.onCustomizeChange}}
			@anchor={{@controller.anchor}}
			@onAnchorReached={{@controller.onAnchorReached}}
		/>
	{{/if}}

	<InfolayersComponent />
	<CommonRotateDeviceComponent />
</template>;

export default ApplicationRouteTemplate;
